/*
 * Copyright (c) 2020 Via Transportation, Inc. All rights reserved.
 *
 * This software is distributed under license and may not be used, copied,
 * modified, or distributed without the express written permission of
 * Via Transportation, Inc.
 */

import AppBar from "@material-ui/core/AppBar";
import Box from "@material-ui/core/Box";
import Button from "@material-ui/core/Button";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import Divider from "@material-ui/core/Divider";
import Drawer from "@material-ui/core/Drawer";
import Grid from "@material-ui/core/Grid";
import Hidden from "@material-ui/core/Hidden";
import IconButton from "@material-ui/core/IconButton";
import Toolbar from "@material-ui/core/Toolbar";
import Typography from "@material-ui/core/Typography";
import { makeStyles, createStyles } from "@material-ui/core/styles";
import React, { ReactNode, useState } from "react";
import { FormattedDate, FormattedMessage } from "react-intl";
import { useDispatch } from "react-redux";
import { Link, useLocation, useNavigate } from "react-router-dom";

import { resetStore } from "store/actions";
import { logAnalyticsEvent } from "common/analytics";
import AnalyticsEventName from "common/analytics/types/AnalyticsEventName";
import { useCommonState, useLarge } from "common/hooks";
import useNotification from "common/hooks/useNotification";
import { ExtendedFetchBaseQueryError } from "common/types/ExtendedFetchBaseQueryError";
import { NotificationMessage, NotificationType } from "common/types/Notification";
import { getErrorName, toSnakeCase } from "common/util";
import { removeCookies } from "pages/LoginWeb/utils";

import { ReactComponent as LogoViaCal } from "resources/LogoViaCal.svg";
import { ReactComponent as MenuIcon } from "resources/MenuIcon.svg";
import { ReactComponent as UserIcon } from "resources/UserIcon.svg";

const useClasses = makeStyles((theme) =>
    createStyles({
        appBarIcon: {
            color: theme.palette.text.primary,
            marginBottom: theme.spacing(3),
            marginRight: theme.spacing(2),
            marginTop: theme.spacing(3),
        },
        appBarTitle: {
            marginRight: "auto",
            userSelect: "none",
        },
        drawerButton: {
            paddingBottom: theme.spacing(4),
            paddingLeft: theme.spacing(3),
            paddingRight: theme.spacing(3),
            paddingTop: theme.spacing(3),
        },
        drawerContainer: {
            flexWrap: "nowrap",
            height: "100%",
        },
        drawerDivider: {
            marginBottom: theme.spacing(2),
        },
        drawerLogo: {
            paddingBottom: theme.spacing(4),
            paddingLeft: theme.spacing(2.5),
            paddingRight: theme.spacing(2.5),
            paddingTop: theme.spacing(3),
        },
        drawerMenuItem: {
            "&:active": {
                backgroundColor: "transparent",
                color: theme.palette.secondary.main,
            },
            "&:hover": {
                backgroundColor: "transparent",
                color: theme.palette.text.secondary,
            },
            borderLeftColor: "transparent",
            borderLeftStyle: "solid",
            borderLeftWidth: theme.spacing(0.5),
            borderRadius: 0,
            fontSize: theme.typography.h2.fontSize,
            fontWeight: theme.typography.fontWeightMedium as any,
            height: theme.spacing(7),
            justifyContent: "left",
            marginBottom: theme.spacing(1),
            marginTop: theme.spacing(1),
            paddingLeft: theme.spacing(2.5),
            paddingRight: theme.spacing(3),
            textAlign: "initial",
        },
        drawerSelectedMenuItem: {
            backgroundColor: theme.palette.primary.light,
            borderLeftColor: theme.palette.primary.main,
            borderLeftStyle: "solid",
            borderLeftWidth: theme.spacing(0.5),
            borderRadius: 0,
            color: theme.palette.primary.main,
            fontSize: theme.typography.h2.fontSize,
            fontWeight: theme.typography.fontWeightMedium as any,
            height: theme.spacing(7),
            justifyContent: "left",
            marginBottom: theme.spacing(1),
            marginTop: theme.spacing(1),
            paddingLeft: theme.spacing(2.5),
            paddingRight: theme.spacing(3),
            textAlign: "initial",
        },
        drawerUser: {
            paddingBottom: theme.spacing(3),
            paddingLeft: theme.spacing(3),
            paddingRight: theme.spacing(3),
        },
        drawerUserName: {
            color: theme.palette.primary.main,
            marginLeft: theme.spacing(1),
        },
    })
);

interface MenuItemProps {
    pageName?: string;
    children: ReactNode;
    onClick: () => void;
}

export const MenuItem = ({ pageName, children, onClick }: MenuItemProps) => {
    const classes = useClasses();
    const large = useLarge();
    const { pathname } = useLocation();

    const onClickMenuItem = (pageName: string) => {
        logAnalyticsEvent({
            info: {
                buttonName: toSnakeCase(pageName),
                buttonType: "menu_item",
                pageName: toSnakeCase(pageName),
                section: toSnakeCase(pageName),
            },
            name: AnalyticsEventName.ButtonTap,
        });

        onClick();
    };

    return (
        <Grid item key={pageName}>
            {pageName ? (
                <Button
                    className={
                        large && `/${pageName}` === pathname
                            ? classes.drawerSelectedMenuItem
                            : classes.drawerMenuItem
                    }
                    component={Link}
                    fullWidth
                    onClick={() => onClickMenuItem(pageName)}
                    to={`/${pageName}`}
                >
                    {children}
                </Button>
            ) : (
                <Button
                    className={classes.drawerMenuItem}
                    fullWidth
                    onClick={() => onClickMenuItem(pathname)}
                >
                    {children}
                </Button>
            )}
        </Grid>
    );
};

function ApplicationHeader() {
    const classes = useClasses();
    const { pathname } = useLocation();
    const navigate = useNavigate();
    const dispatch = useDispatch();

    const large = useLarge();
    const { authentication, configuration } = useCommonState();
    const { enqueueNotification, dequeueNotification } = useNotification();

    const [drawerOpen, setDrawerOpen] = useState(false);
    const handleDrawerToggle = () => {
        setDrawerOpen(!drawerOpen);
    };

    const pageTitle = {
        "/schedule": configuration.isTaaS ? (
            <FormattedMessage id={"shift.title"} defaultMessage="Your reserved driving times" />
        ) : (
            <FormattedMessage id={"shift.title.internal"} defaultMessage="Your scheduled shifts" />
        ),
        "/absence-management": <FormattedMessage id="absence.title" defaultMessage="My absences" />,
        "/contact-us": <FormattedMessage id="contact.title" defaultMessage="Contact us" />,
    };

    const handleLogout = async () => {
        logAnalyticsEvent({
            info: {
                errorMessage: "",
                flowName: "logout",
                flowSubsection: "",
                isError: false,
                payload: {
                    phoneNumber: authentication?.username,
                },
            },
            name: AnalyticsEventName.UserAuthentication,
        });

        try {
            removeCookies();
            dispatch(resetStore());
            navigate("/");

            dequeueNotification(NotificationMessage.Logout);
        } catch (error) {
            const logoutNameError = getErrorName(error as ExtendedFetchBaseQueryError);
            enqueueNotification({
                error: error as ExtendedFetchBaseQueryError | Error,
                message: logoutNameError,
                type: NotificationType.ERROR,
            });
        }
    };

    const [logoutConfirmOpen, setLogoutConfirmOpen] = useState(false);
    const closeLogoutConfirm = () => setLogoutConfirmOpen(false);
    const openLogoutConfirm = () => setLogoutConfirmOpen(true);

    const onClickApply = () => {
        /* istanbul ignore next */
        const shiftMenuButtonName = configuration?.isTaaS ? "claim_shifts" : "apply_to_new_shifts";

        logAnalyticsEvent({
            info: {
                buttonName: shiftMenuButtonName,
                buttonType: "button",
                pageName: toSnakeCase(pathname),
                section: toSnakeCase(pathname),
            },
            name: AnalyticsEventName.ButtonTap,
        });

        navigate("/schedule", {
            state: {
                apply: new Date(),
            },
        });
    };

    return (
        <>
            <Hidden lgUp>
                <AppBar position="fixed">
                    <Toolbar>
                        <IconButton className={classes.appBarIcon} onClick={handleDrawerToggle}>
                            <MenuIcon />
                        </IconButton>
                        <Typography className={classes.appBarTitle} variant="h3">
                            {pageTitle[pathname] ? (
                                pageTitle[pathname]
                            ) : (
                                <FormattedMessage
                                    id="application.error.title"
                                    defaultMessage="Error"
                                />
                            )}
                        </Typography>
                    </Toolbar>
                </AppBar>
                <Box pb={9} />
            </Hidden>
            <Drawer
                ModalProps={{
                    keepMounted: true,
                }}
                PaperProps={{
                    elevation: large ? 4 : 16,
                }}
                onClose={handleDrawerToggle}
                open={drawerOpen}
                variant={large ? "permanent" : "temporary"}
            >
                <Grid className={classes.drawerContainer} container direction="column">
                    <Grid item>
                        <Box className={classes.drawerLogo}>
                            <LogoViaCal />
                        </Box>
                    </Grid>
                    <Grid item>
                        <Grid
                            alignItems="center"
                            className={classes.drawerUser}
                            container
                            justifyContent="flex-start"
                        >
                            <Grid item>
                                <UserIcon />
                            </Grid>
                            <Grid item>
                                <Typography className={classes.drawerUserName} variant="h1">
                                    <FormattedMessage
                                        defaultMessage="Hi {value}!"
                                        id="application.user"
                                        values={{
                                            value: configuration.driverFullName,
                                        }}
                                    />
                                </Typography>
                            </Grid>
                        </Grid>
                    </Grid>
                    <Grid item>
                        <Divider className={classes.drawerDivider} />
                    </Grid>
                    <Hidden mdDown>
                        <Grid item>
                            {configuration.shiftsConfig?.isEnabledViewingClaimableShifts && (
                                <Grid className={classes.drawerButton} container direction="column">
                                    <Grid item key="apply">
                                        <Button
                                            onClick={onClickApply}
                                            fullWidth
                                            variant="contained"
                                        >
                                            <FormattedMessage
                                                id="shift.applyButton"
                                                defaultMessage="Reserve driving time"
                                            />
                                        </Button>
                                    </Grid>
                                </Grid>
                            )}
                        </Grid>
                    </Hidden>

                    <MenuItem pageName="schedule" onClick={handleDrawerToggle}>
                        {configuration.isTaaS ? (
                            <FormattedMessage
                                id="shift.menu"
                                defaultMessage="Reserved driving times"
                            />
                        ) : (
                            <FormattedMessage
                                id="shift.menu.internal"
                                defaultMessage="Scheduled shifts"
                            />
                        )}
                    </MenuItem>

                    {configuration?.shiftsConfig?.isEnabledRequestAbsence && (
                        <MenuItem pageName="absence-management" onClick={handleDrawerToggle}>
                            <FormattedMessage id="absence.menu" defaultMessage="Manage absences" />
                        </MenuItem>
                    )}

                    <MenuItem pageName="contact-us" onClick={handleDrawerToggle}>
                        <FormattedMessage id="contact.menu" defaultMessage="Contact us" />
                    </MenuItem>

                    {!configuration.isDriverApp && (
                        <MenuItem onClick={openLogoutConfirm}>
                            <FormattedMessage defaultMessage="Logout" id="application.logout" />
                        </MenuItem>
                    )}

                    {process.env.REACT_APP_GIT_COMMIT_DATE &&
                        process.env.REACT_APP_GIT_COMMIT_TAG && (
                            <>
                                <Grid item style={{ flexGrow: 1 }} />
                                <Grid item>
                                    <Box m={3}>
                                        <Typography color="textSecondary" variant="body2">
                                            v{process.env.REACT_APP_GIT_COMMIT_TAG}
                                            <br />
                                            <FormattedDate
                                                year="numeric"
                                                month="long"
                                                day="2-digit"
                                                value={process.env.REACT_APP_GIT_COMMIT_DATE}
                                            />
                                        </Typography>
                                    </Box>
                                </Grid>
                            </>
                        )}
                </Grid>
            </Drawer>
            <Dialog open={logoutConfirmOpen} onClose={closeLogoutConfirm} maxWidth="md">
                <DialogContent>
                    <Box textAlign="center" px={2} py={2}>
                        <Typography variant="h3">
                            <Box fontWeight="fontWeightRegular">
                                <FormattedMessage
                                    defaultMessage="Are you sure you want to log out?"
                                    id="application.logoutConfirm"
                                />
                            </Box>
                        </Typography>
                    </Box>
                </DialogContent>
                <Divider />
                <DialogActions>
                    <Button
                        color="secondary"
                        onClick={() => {
                            logAnalyticsEvent({
                                info: {
                                    buttonName: "cancel_logout",
                                    buttonType: "button",
                                    pageName: toSnakeCase(pathname),
                                    section: toSnakeCase(pathname),
                                },
                                name: AnalyticsEventName.ButtonTap,
                            });
                            closeLogoutConfirm();
                        }}
                    >
                        <FormattedMessage defaultMessage="Cancel" id="application.logoutCancel" />
                    </Button>
                    <Button
                        onClick={() => {
                            logAnalyticsEvent({
                                info: {
                                    buttonName: "confirm_logout",
                                    buttonType: "button",
                                    pageName: toSnakeCase(pathname),
                                    section: toSnakeCase(pathname),
                                },
                                name: AnalyticsEventName.ButtonTap,
                            });
                            handleLogout();
                        }}
                        color="primary"
                        autoFocus
                    >
                        <FormattedMessage defaultMessage="Logout" id="application.logout" />
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
}

export default ApplicationHeader;
